import { useEffect, useMemo } from "react";

import { useSiteNavigation } from "./useSiteNavigation";
import { SITE_EMPTY_NAME } from "../constants/uiConstants";
import { useAuth } from "../contexts/AuthContext";

const getSiteDisplayName = (
  name: string | null | undefined,
  id: string,
): string => {
  return name?.trim() || `${SITE_EMPTY_NAME} (${id})`;
};

export function useConfiguredSiteRedirect() {
  const { sites } = useAuth();
  const { siteIdFromParams, navigateToSite } = useSiteNavigation();
  const siteId = siteIdFromParams;

  const availableSites = useMemo(() => {
    return sites
      .map((site) => ({
        ...site,
        name: getSiteDisplayName(site.name, site.id),
      }))
      .sort((a, b) => {
        const aIsEmpty = a.name.startsWith(SITE_EMPTY_NAME);
        const bIsEmpty = b.name.startsWith(SITE_EMPTY_NAME);

        if (aIsEmpty && !bIsEmpty) return -1;
        if (!aIsEmpty && bIsEmpty) return 1;

        return a.name.localeCompare(b.name);
      });
  }, [sites]);

  useEffect(() => {
    if (!siteId) return;

    const currentSite = availableSites.find((s) => s.id === siteId);

    if (currentSite?.name?.startsWith(SITE_EMPTY_NAME)) {
      const firstEnabledSite = availableSites.find(
        (site) => !site.name.startsWith(SITE_EMPTY_NAME),
      );

      if (firstEnabledSite && firstEnabledSite.id !== siteId) {
        navigateToSite(firstEnabledSite.id);
      }
    }
  }, [siteId, availableSites, navigateToSite]);
}
